import { useCallback, useEffect, useRef } from 'react'
import { useLocale } from '../../i18n/context'
import { type LearningNextCard } from '../../types'
import MasteryBadge from './MasteryBadge'
import BracketCorners from './BracketCorners'
import ClickableText from './ClickableText'
import FillInBlankSentence from './FillInBlankSentence'
import SpeakerButton from './SpeakerButton'
import WordReferenceTrigger from './WordReferenceTrigger'
import { buildFillInBlank, type FibSegment } from '../../utils/learningUtils'
import { findTermSide, isTermSide } from '../../utils/cardSides'
import { learnDefinitionSide, learnExampleSide } from '../../utils/learnCardFields'

export default function PreviousCardOverlay({
  card,
  onClose,
  speak,
  isSpeechSupported,
  isSpeaking,
  showTranslations,
  onOpenReference,
  onOpenDefinition,
}: {
  card: LearningNextCard
  onClose: () => void
  speak: (word: string, onEnd?: () => void) => void
  isSpeechSupported: boolean
  isSpeaking: boolean
  showTranslations: boolean
  /** Opens the Examples / Reference / AI panel for THIS previous card. */
  onOpenReference?: (term: string, sentence?: string) => void
  /** Opens the Definition bottom sheet for THIS previous card. Receives the
   *  prev card's full Definition and (optionally) Example so the sheet can
   *  render both sections for long user content. */
  onOpenDefinition?: (definition: string, example?: string) => void
}) {
  const { tr } = useLocale()
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  const termSide = findTermSide(card.other_sides)
  // ADR-104: the example and definition VALUE sides — a blank field in front
  // does not empty the look-back. The prompt is listed first for the
  // definition: a Definition prompt is already the card's value. With no
  // definition value, the prompt stands in as before.
  const exampleSide = learnExampleSide(card)
  const definitionSide = learnDefinitionSide(card) ??
    (!isTermSide(card.prompt) ? card.prompt : null)

  // If the example has an underscore blank, fill it with the term for the review view
  const exampleFib = exampleSide && termSide
    ? buildFillInBlank(exampleSide.content, termSide.content)
    : null

  // TTS must match what the user sees. When the example renders as a
  // fill-in-the-blank with the term substituted in, speaking the raw
  // exampleSide.content would read "...she finally achieved underscore
  // underscore of the instrument". Reconstruct the rendered string from the
  // FibSegment[] so audio and DOM agree (Prompt G).
  const renderFib = (segs: FibSegment[]): string =>
    segs.map((s) => (s.kind === 'text' ? s.text : s.match)).join('')
  const textToSpeak = exampleFib
    ? renderFib(exampleFib)
    : exampleSide?.content ?? definitionSide?.content ?? ''

  // Swipe-left → return to current card. Mirrors LearnPage's swipe-right
  // gate (Prompt F): ≥60px primarily-horizontal delta, and the gesture must
  // not originate inside a button/trigger (so tapping the Definition button
  // or the Back button still fires its onClick instead of being swallowed
  // by the swipe handler). The button continues to work — swipe is additive.
  const SWIPE_DX_MIN = 60
  const SWIPE_RATIO_MIN = 2
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null)
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const target = e.target as HTMLElement | null
    // Ignore touches originating on actionable controls so their onClick wins.
    if (target?.closest('button, a, [role="button"]')) {
      swipeStartRef.current = null
      return
    }
    const t = e.touches[0]
    if (!t) return
    swipeStartRef.current = { x: t.clientX, y: t.clientY }
  }, [])
  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    const start = swipeStartRef.current
    swipeStartRef.current = null
    if (!start) return
    const t = e.changedTouches[0]
    if (!t) return
    const dx = t.clientX - start.x
    const dy = t.clientY - start.y
    // Swipe-left (negative dx) returns to current. Reuses the same onClose
    // handler the button uses — no duplicate navigation logic.
    if (dx <= -SWIPE_DX_MIN && Math.abs(dx) >= Math.abs(dy) * SWIPE_RATIO_MIN) {
      onClose()
    }
  }, [onClose])
  const onTouchCancel = useCallback(() => { swipeStartRef.current = null }, [])

  return (
    <div
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchCancel}
      style={{
        // Fills the motion.div wrapper supplied by LearnPage (the wrapper
        // owns position:fixed inset:0 + z-index + the slide transform).
        width: '100%', height: '100%', overflowY: 'auto',
        background: 'var(--bg-page)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        // Comfortable padding from system nav bar / home indicator so the
        // bottom "Back to current card" button is never clipped (Prompt G).
        padding: '0 16px max(28px, env(safe-area-inset-bottom))',
      }}>
      {/* Top bar — safe-area-aware top padding so the row clears the system
          status bar / notch on mobile. */}
      <div style={{
        width: '100%', maxWidth: 640,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 'max(16px, env(safe-area-inset-top)) 0 12px',
      }}>
        <button
          onClick={onClose}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-secondary)', fontSize: 14,
            padding: '12px 8px', minHeight: 44,
          }}
        >
          {tr.learn.backToCurrent}
        </button>
        {/* Mode badge — makes it unambiguous the user is reviewing, not answering */}
        <span style={{
          fontSize: 11, fontWeight: 600, color: 'var(--accent)',
          textTransform: 'uppercase', letterSpacing: '0.1em',
          padding: '5px 12px', borderRadius: 50,
          background: 'var(--accent-soft)',
        }}>
          {tr.learn.previousCardBadge}
        </span>
      </div>

      {/* Card box — accent border signals review mode */}
      <div style={{
        width: '100%', maxWidth: 640,
        background: 'var(--bg-surface)', borderRadius: 16,
        border: '1.5px solid var(--accent)', boxShadow: 'var(--shadow-flashcard)',
        padding: '28px 20px 24px', position: 'relative',
      }}>
        <BracketCorners />

        {/* Term (revealed) */}
        {termSide && (
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{
              fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
              textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6,
            }}>
              {tr.learn.termLabel}
            </div>
            <div style={{
              fontSize: 28, fontFamily: 'var(--font-content)', fontWeight: 600,
              color: 'var(--text-primary)', lineHeight: 1.3,
            }}>
              <ClickableText text={termSide.content} speak={speak} isSpeechSupported={isSpeechSupported} />
            </div>
          </div>
        )}

        {/* Translation — shown when a word-entry translation exists and user has translations on */}
        {card.translation && showTranslations && (
          <div style={{ textAlign: 'center', marginBottom: 16, marginTop: termSide ? -8 : 0 }}>
            <span style={{
              fontSize: 14, color: 'var(--text-secondary)',
              fontFamily: 'var(--font-content)', fontStyle: 'italic',
              lineHeight: 1.4,
            }}>
              <ClickableText text={card.translation} speak={speak} isSpeechSupported={isSpeechSupported} />
            </span>
          </div>
        )}

        {/* Example sentence — blank filled with term if applicable */}
        {exampleSide && (
          <div style={{ marginBottom: 18 }}>
            <div style={{
              fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
              textTransform: 'uppercase', letterSpacing: '0.08em',
              textAlign: 'center', marginBottom: 8,
            }}>
              {tr.learn.exampleLabel}
            </div>
            {exampleFib ? (
              <FillInBlankSentence
                segments={exampleFib}
                reveal="full"
                revealColor="var(--accent)"
                speak={speak}
                isSpeechSupported={isSpeechSupported}
                fontSize={22}
              />
            ) : (
              <p style={{
                margin: 0, fontSize: 22, fontFamily: 'var(--font-content)',
                color: 'var(--text-primary)', textAlign: 'center',
                lineHeight: 1.6, wordBreak: 'break-word',
              }}>
                <ClickableText text={exampleSide.content} speak={speak} isSpeechSupported={isSpeechSupported} />
              </p>
            )}
          </div>
        )}

        {/* Definition */}
        {definitionSide && (
          <div style={{ marginBottom: 8 }}>
            <div style={{
              fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
              textTransform: 'uppercase', letterSpacing: '0.08em',
              textAlign: 'center', marginBottom: 6,
            }}>
              {tr.learn.definitionLabel}
            </div>
            <p style={{
              margin: 0, fontSize: 15, fontFamily: 'var(--font-content)',
              color: 'var(--text-secondary)', textAlign: 'center', lineHeight: 1.5,
            }}>
              <ClickableText text={definitionSide.content} speak={speak} isSpeechSupported={isSpeechSupported} />
            </p>
          </div>
        )}

        {/* Speak button — plays the full example/definition sentence */}
        {isSpeechSupported && textToSpeak && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
            <SpeakerButton onClick={() => speak(textToSpeak)} isSpeaking={isSpeaking} />
          </div>
        )}

        {/* Definition trigger — parity with the active card. Re-opens the
            same DefinitionBottomSheet at LearnPage root, scoped to this card. */}
        {onOpenDefinition && definitionSide && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 14 }}>
            <button
              onClick={() => onOpenDefinition(definitionSide.content, exampleSide?.content)}
              style={{
                padding: '10px 16px', borderRadius: 50,
                border: '1px solid var(--border)',
                background: 'transparent',
                color: 'var(--text-secondary)',
                fontSize: 13, fontWeight: 500, cursor: 'pointer', minHeight: 44,
              }}
            >
              {tr.learn.definitionLabel}
            </button>
          </div>
        )}

        {/* Book-icon trigger — same as the active card. Opens Examples /
            Reference / AI panel for the previous card's term. */}
        {onOpenReference && termSide && (
          <WordReferenceTrigger
            visible={true}
            onClick={() => onOpenReference(termSide.content, exampleSide?.content)}
          />
        )}
      </div>

      {/* Mastery indicator — Wave 3 §1/§5, ADR-014 amendment: eight-segment magnitude bar.
          Level encodes fill count, not hue. No colored dot, no emoji, no red. No
          adjacent text label either — the level is a number, not a word; the
          badge's own aria-label ("Mastery: N of 8") is the accessible name. */}
      <div style={{
        width: '100%', maxWidth: 640, marginTop: 14,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <MasteryBadge level={card.mastery_level} size="dot" />
      </div>

      {/* Back button (bottom, for thumb reach on mobile) */}
      <div style={{ marginTop: 28, marginBottom: 12 }}>
        <button
          onClick={onClose}
          style={{
            padding: '14px 40px', borderRadius: 50,
            border: '1px solid var(--border)', background: 'transparent',
            color: 'var(--text-primary)', fontSize: 15, fontWeight: 500,
            cursor: 'pointer', minHeight: 44,
          }}
        >
          {tr.learn.backToCurrentCard}
        </button>
      </div>
    </div>
  )
}
